import {BitmapText, Container, FederatedPointerEvent, Graphics, Rectangle} from "pixi.js";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {ElevationApp} from "../../ElevationApp";
import {inches, lineBlueGrey, lineBlueHeavy, lineBlueLight} from "../../const";
import {sticksById, joinedRootId} from "../../model/frame/MdFrameBlock";
import {RenderAnchorSection} from "../../../../../../api/ElevationTool";
import {StickMaterial} from "./StickMaterial";
import {OutlineSide} from "./BlockOps";
import {AnchorSectionLayout, anchorStickSides, anchorTarget, layoutAnchorSections, moveAnchorDivider, pieceTakesAnchors} from "./Anchors";

// sizes in drawing units.
const BUMP_ALONG = 1 * inches;
const BUMP_OUT = 0.5 * inches;
const LABEL_GAP = 0.5 * inches;
const LABEL_FONT = 40;
const STRIP_GAP = 0.5 * inches;
const STRIP_WIDTH = 1 * inches;
const DIVIDER_GRAB = 0.75 * inches;

// one anchored stick as drawn: the pieces it draws as, their extent along the stick's axis
// (parent coords) and its sections laid out over that extent.
type AnchorRun = {
    stickId: string;
    side: OutlineSide;
    vertical: boolean;
    pieces: {mat: StickMaterial; start: number; end: number}[];
    min: number;
    max: number;
    sections: AnchorSectionLayout[];
};

// the frame anchors drawn on the outline sticks: a bump on the stick's outer side for each
// anchor, a product label per section and a line at each section boundary. A mullion beside
// a void draws them only on its pieces beside the void. In anchor setup
// it adds a clickable strip per section and lets a boundary drag. A stick that draws as
// several pieces lays its anchors out over all of them; each piece draws the marks inside it.
// Owned by FrameGridView and drawn after each layout.
export class AnchorMarks extends DrawFrmThing {
    private elev: ElevationApp;
    private sticks: () => StickMaterial[];

    // labels and hit targets, rebuilt on each draw.
    private overlay = new Container();

    // whether the last draw drew any mark.
    private drewAny = false;

    // the divider drag in progress, or null. See startDrag.
    private drag: {run: AnchorRun; index: number; startGlobal: number; startAlong: number; scale: number} | null = null;
    private onDragMove = (e: FederatedPointerEvent) => this.dragMove(e);
    private onDragEnd = () => this.endDrag();

    constructor(elev: ElevationApp, sticks: () => StickMaterial[]) {
        super();
        this.elev = elev;
        this.sticks = sticks;
        this.graphic.addChild(this.overlay);

        const vs = elev.viewSettings;
        const redraw = () => this.draw();
        this.trackSubscription(vs.selection.subscribe(redraw));
        this.trackSubscription(vs.anchorSetup.subscribe(redraw));
        this.trackSubscription(vs.showAnchors.subscribe(redraw));
        this.trackSubscription(elev.anchorProducts.subscribe(redraw));
    }

    // every anchored stick that draws, from the last layout.
    private runs(): AnchorRun[] {
        const spec = this.elev.frameSpec;
        const model = sticksById(spec.frameBlocks);
        const sides = anchorStickSides(spec);

        const byRoot = new Map<string, StickMaterial[]>();
        for(const mat of this.sticks()) {
            if(mat.destroyed || !mat.graphic.visible || mat.length.get() <= 0) continue;
            const root = joinedRootId(model, mat.stickId);
            if(!model.get(root)?.anchors || !sides.has(root) || !pieceTakesAnchors(spec, root, mat.voidSide)) continue;
            byRoot.set(root, [...(byRoot.get(root) ?? []), mat]);
        }

        const out: AnchorRun[] = [];
        for(const [stickId, mats] of byRoot) {
            const vertical = mats[0].orientation === "vertical";
            const pieces = mats
                .filter(m => m.orientation === mats[0].orientation)
                .map(mat => {
                    const start = vertical ? mat.positionY : mat.positionX;
                    return {mat, start, end: start + mat.length.get()};
                })
                .sort((a, b) => a.start - b.start);

            const min = pieces[0].start;
            const max = Math.max(...pieces.map(p => p.end));
            out.push({
                stickId,
                side: sides.get(stickId)!,
                vertical,
                pieces,
                min,
                max,
                sections: layoutAnchorSections(model.get(stickId)!.anchors!.sections, max - min),
            });
        }
        return out;
    }

    // a stick's drawn length and its sections laid out over it, from the last layout. null
    // when the stick has no anchors or doesn't draw.
    layoutOf(stickId: string): {length: number; sections: AnchorSectionLayout[]} | null {
        const run = this.runs().find(r => r.stickId === stickId);
        if(!run) return null;
        return {length: run.max - run.min, sections: run.sections};
    }

    // the stick whose anchors the drawn piece `pieceId` edits, or null when the piece can't
    // take anchors (see anchorTarget).
    targetOf(pieceId: string): string | null {
        const mat = this.sticks().find(m => !m.destroyed && m.id === pieceId);
        return mat ? anchorTarget(this.elev.frameSpec, mat.stickId, mat.voidSide) : null;
    }

    // the anchor sections the drawing holds, with their resolved qty, for the headless
    // render to report.
    measure(): RenderAnchorSection[] {
        return this.runs().flatMap(run => run.sections.map(s => ({
            stickId: run.stickId,
            side: run.side,
            product: s.product,
            qty: s.qty,
            lengthIn: (s.end - s.start) / inches,
        })));
    }

    draw() {
        if(this.destroyed) return;

        const g = this.lines;
        g.clear();
        for(const c of this.overlay.removeChildren()) c.destroy({children: true});

        this.drewAny = false;
        const vs = this.elev.viewSettings;
        const setup = !!vs.anchorSetup.value;
        if(!setup && !vs.showAnchors.value) return;

        for(const run of this.runs()) {
            this.drawRun(run, setup);
            this.drewAny = true;
        }
    }

    // how far the marks from the last draw reach past each side of `rect` (parent coords).
    extentBeyond(rect: {x: number; y: number; width: number; height: number}): {top: number; left: number; right: number} {
        if(!this.drewAny || this.destroyed) return {top: 0, left: 0, right: 0};

        const b = this.graphic.getLocalBounds();
        return {
            top: Math.max(0, rect.y - b.y),
            left: Math.max(0, rect.x - b.x),
            right: Math.max(0, b.x + b.width - (rect.x + rect.width)),
        };
    }

    private drawRun(run: AnchorRun, setup: boolean) {
        const g = this.lines;
        const out = run.side === "top" || run.side === "left" ? -1 : 1;
        const face = run.side === "top" || run.side === "left" ? "left" : "right";

        // the outer face's cross position at `along` (parent coords), or null in a gap
        // between pieces.
        const faceAt = (along: number): number | null => {
            const p = run.pieces.find(p => along >= p.start - 0.01 && along <= p.end + 0.01);
            if(!p) return null;
            const base = run.vertical ? p.mat.positionX : p.mat.positionY;
            return base + p.mat.facesAt(along - p.start)[face];
        };

        // a point at `along` on the stick's axis and `cross` across it, in parent coords.
        const pt = (along: number, cross: number): [number, number] => run.vertical ? [cross, along] : [along, cross];

        g.lineStyle(1, lineBlueHeavy, 1);
        for(const s of run.sections) {
            for(const pos of s.positions) {
                const a0 = run.min + pos - BUMP_ALONG / 2;
                const a1 = run.min + pos + BUMP_ALONG / 2;
                const f0 = faceAt(a0);
                const f1 = faceAt(a1);
                if(f0 === null || f1 === null) continue;

                g.beginFill(lineBlueHeavy, 1);
                g.drawPolygon([
                    ...pt(a0, f0), ...pt(a1, f1),
                    ...pt(a1, f1 + out * BUMP_OUT), ...pt(a0, f0 + out * BUMP_OUT),
                ]);
                g.endFill();
            }
        }

        const labels = run.sections.map(s => this.addLabel(run, s, faceAt, pt, out));
        const labelDepth = Math.max(0, ...labels.map(l => l ? (run.vertical ? l.width : l.height) : 0));

        // a line across the stick at each section boundary.
        const thickness = run.pieces[0].mat.crossThickness();
        g.lineStyle(2, lineBlueHeavy, 1);
        for(const s of run.sections.slice(0, -1)) {
            const along = run.min + s.end;
            const f = faceAt(along);
            if(f === null) continue;
            g.moveTo(...pt(along, f - out * thickness));
            g.lineTo(...pt(along, f + out * BUMP_OUT));
            if(setup && !this.elev.readOnly) this.addDividerHandle(run, s.index, along, f, out, thickness, pt);
        }

        if(setup && run.sections.length > 1) {
            this.addStrip(run, faceAt, pt, out, BUMP_OUT + LABEL_GAP + labelDepth + STRIP_GAP);
        }
    }

    // the product label of a section, centred on it outside the bumps.
    private addLabel(
        run: AnchorRun,
        s: AnchorSectionLayout,
        faceAt: (along: number) => number | null,
        pt: (along: number, cross: number) => [number, number],
        out: number,
    ): BitmapText | null {
        const mid = run.min + (s.start + s.end) / 2;
        const f = faceAt(mid);
        if(f === null) return null;

        const t = new BitmapText(this.productName(s.product), {fontName: "Arial", fontSize: LABEL_FONT});
        t.tint = lineBlueHeavy;
        // the anchor point sits on the side of the text nearest the stick.
        if(run.vertical) t.anchor.set(out < 0 ? 1 : 0, 0.5);
        else t.anchor.set(0.5, out < 0 ? 1 : 0);
        t.position.set(...pt(mid, f + out * (BUMP_OUT + LABEL_GAP)));
        this.overlay.addChild(t);
        return t;
    }

    private productName(product: number): string {
        const p = this.elev.anchorProducts.value?.products.find(p => p.id === product);
        return p?.name || "#" + product;
    }

    // the clickable strip outside the labels, one segment per section. A tap selects the section.
    private addStrip(
        run: AnchorRun,
        faceAt: (along: number) => number | null,
        pt: (along: number, cross: number) => [number, number],
        out: number,
        offset: number,
    ) {
        const vs = this.elev.viewSettings;
        // a straight strip, clear of the outermost point of the face (a sloped stick).
        const faces = run.pieces.flatMap(p => [faceAt(p.start), faceAt(p.end)]).filter((f): f is number => f !== null);
        const base = out < 0 ? Math.min(...faces) : Math.max(...faces);
        const c0 = base + out * offset;
        const c1 = c0 + out * STRIP_WIDTH;

        for(const s of run.sections) {
            const selected = vs.selection.value.some(sel => sel.type === "anchor-section" && sel.stickId === run.stickId && sel.index === s.index);
            const a0 = run.min + s.start;
            const a1 = run.min + s.end;

            const seg = new Graphics();
            seg.lineStyle(1, lineBlueHeavy, 1);
            seg.beginFill(selected ? lineBlueLight : lineBlueGrey, selected ? 0.6 : 0.8);
            seg.drawPolygon([...pt(a0, c0), ...pt(a1, c0), ...pt(a1, c1), ...pt(a0, c1)]);
            seg.endFill();

            seg.eventMode = "static";
            seg.cursor = "pointer";
            seg.on("pointerdown", e => e.stopPropagation());
            seg.on("pointertap", e => {
                e.stopPropagation();
                vs.selectItem({type: "anchor-section", id: `anchor:${run.stickId}:${s.index}`, stickId: run.stickId, index: s.index}, false);
            });
            this.overlay.addChild(seg);
        }
    }

    // a grab area over a section boundary that drags it along the stick.
    private addDividerHandle(
        run: AnchorRun,
        index: number,
        along: number,
        face: number,
        out: number,
        thickness: number,
        pt: (along: number, cross: number) => [number, number],
    ) {
        const [x0, y0] = pt(along - DIVIDER_GRAB, face - out * thickness);
        const [x1, y1] = pt(along + DIVIDER_GRAB, face + out * BUMP_OUT);

        const handle = new Container();
        handle.eventMode = "static";
        handle.cursor = run.vertical ? "ns-resize" : "ew-resize";
        handle.hitArea = new Rectangle(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
        handle.on("pointerdown", e => this.startDrag(e, run, index, along - run.min));
        handle.on("pointertap", e => e.stopPropagation());
        this.overlay.addChild(handle);
    }

    private startDrag(e: FederatedPointerEvent, run: AnchorRun, index: number, along: number) {
        e.stopPropagation();
        const wt = this.graphic.worldTransform;

        this.drag = {
            run,
            index,
            startGlobal: run.vertical ? e.global.y : e.global.x,
            startAlong: along,
            scale: (run.vertical ? wt.d : wt.a) || 1,
        };

        const draw = this.elev.draw;
        draw.setPanSuspended(true);
        draw.pixi.stage.eventMode = "static";
        draw.pixi.stage.on("globalpointermove", this.onDragMove);
        window.addEventListener("pointerup", this.onDragEnd);
        window.addEventListener("pointercancel", this.onDragEnd);
    }

    private dragMove(e: FederatedPointerEvent) {
        const d = this.drag;
        if(!d || this.destroyed) return;

        const global = d.run.vertical ? e.global.y : e.global.x;
        const along = d.startAlong + (global - d.startGlobal) / d.scale;
        if(moveAnchorDivider(this.elev.frameSpec, d.run.stickId, d.index, along, d.run.max - d.run.min)) this.draw();
    }

    private endDrag() {
        if(!this.drag) return;
        this.drag = null;

        const draw = this.elev.draw;
        draw.pixi.stage.off("globalpointermove", this.onDragMove);
        window.removeEventListener("pointerup", this.onDragEnd);
        window.removeEventListener("pointercancel", this.onDragEnd);
        draw.setPanSuspended(false);

        // the whole drag is one edit.
        this.elev.frameSpec.frameBlocksChanged.emit(false);
    }

    destroy() {
        this.endDrag();
        super.destroy();
    }
}
